import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { X } from "lucide-react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { showSuccess, showError } from "../../utils/toast";
import getActiveEmailTemplates from "../../api/getActiveEmailTemplates";
import previewChasingEmail from "../../api/previewChasingEmail";
import sendChasingEmail from "../../api/sendChasingEmail";

const schema = yup.object().shape({
  template: yup.object().required("Template is required").nullable(),
});

// Bulk "Chase Documents" popup for multiple clients selected from the
// Clients table. Reuses the exact same chasing APIs as the single-client
// flow (getActiveEmailTemplates / previewChasingEmail / sendChasingEmail) —
// no new email-sending logic — just calls sendChasingEmail once per
// chasable client. Only shows a pending COUNT per client, never document
// names, per the bulk-mode requirement.
const BulkDocumentChasingPopup = ({ open, onClose, clients, setRowSelection }) => {
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);

  const { token } = useContext(AuthContext);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      template: null,
      subjectPreview: "",
      bodyPreview: "",
    },
  });

  const chasableClients = (clients || [])
    .map((client) => ({
      ...client,
      pending: Math.max(
        0,
        (Number(client.requested_count) || 0) - (Number(client.uploaded_count) || 0),
      ),
    }))
    .filter((client) => client.pending > 0);

  useEffect(() => {
    if (!open) return;
    reset();
    const loadTemplates = async () => {
      const res = await getActiveEmailTemplates({ token });
      if (res?.code === 200) {
        setTemplates(res.data || []);
      }
    };
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleTemplateChange = async (selectedTemplate) => {
    if (!selectedTemplate || chasableClients.length === 0) {
      setValue("subjectPreview", "");
      setValue("bodyPreview", "");
      return;
    }
    // Preview is generated for the first chasable client only — each
    // recipient still receives their own personalized values on send.
    const res = await previewChasingEmail({
      token,
      contact_id: chasableClients[0].contact_id,
      template_id: selectedTemplate.id,
    });
    if (res?.code === 200) {
      setValue("subjectPreview", res.data.subject);
      setValue("bodyPreview", res.data.body);
    } else {
      showError("Unable to generate preview.");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const onSubmit = async (data) => {
    if (chasableClients.length === 0) return;
    setSending(true);
    try {
      const results = await Promise.allSettled(
        chasableClients.map((client) =>
          sendChasingEmail({
            token,
            contact_id: client.contact_id,
            template_id: data.template.id,
            recipient_email: client.email_id,
          }),
        ),
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled" && r.value?.code === 200,
      ).length;
      const failureCount = chasableClients.length - successCount;

      if (successCount > 0) {
        showSuccess(
          `Reminder email sent to ${successCount} of ${chasableClients.length} client(s).`,
        );
      }
      if (failureCount > 0) {
        showError(`Failed to send to ${failureCount} client(s).`);
      }

      if (successCount > 0) {
        setRowSelection?.({});
        onClose();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { maxHeight: "85vh" } }}
    >
      <div className="flex gap-3 items-center px-4 py-2 relative">
        <DialogTitle className="text-[#1a6b7c] !text-2xl font-medium p-0">
          Chase Documents
        </DialogTitle>
        <X
          size={24}
          onClick={handleClose}
          className="bg-[#1a6b7c] rounded-full p-1 text-white cursor-pointer absolute right-4 top-3"
        />
      </div>

      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Selected Clients
        </Typography>

        {chasableClients.length === 0 ? (
          <Typography color="text.secondary" sx={{ my: 2 }}>
            None of the selected clients have pending documents.
          </Typography>
        ) : (
          <>
            <div className="flex flex-wrap content-start gap-2 max-h-[160px] overflow-y-auto pr-1 mb-4">
              {chasableClients.map((client) => (
                <span
                  key={client.contact_id}
                  className="bg-gray-100 rounded-full px-3 py-1.5 text-sm whitespace-nowrap"
                >
                  <span className="font-medium">{client.name}</span> ({client.pending})
                </span>
              ))}
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="template"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={templates}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    value={field.value || null}
                    onChange={(e, value) => {
                      field.onChange(value);
                      handleTemplateChange(value);
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Email Template"
                        variant="outlined"
                        margin="normal"
                        error={!!errors.template}
                        helperText={errors.template?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="subjectPreview"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Subject Preview (first client)"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    InputProps={{ readOnly: true }}
                  />
                )}
              />

              <Controller
                name="bodyPreview"
                control={control}
                render={({ field }) => (
                  <div className="mt-2">
                    <Typography variant="body2" color="text.secondary">
                      Body Preview (first client — each recipient receives
                      personalized values)
                    </Typography>
                    <div
                      className="border rounded p-2 text-sm"
                      style={{ minHeight: "100px", maxHeight: "200px", overflowY: "auto" }}
                      dangerouslySetInnerHTML={{ __html: field.value || "" }}
                    />
                  </div>
                )}
              />

              <DialogActions sx={{ px: 0, mt: 2 }}>
                <Button onClick={handleClose} color="inherit">
                  Cancel
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={sending}>
                  Send Email
                </Button>
              </DialogActions>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkDocumentChasingPopup;
